import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {LocalStorageAccessTokenService} from "../login/local-storage-access-token.service";
import {AiChatContext} from "../../generated-sources/api/ai/model/ai-chat-context";
import {AiEntityReference} from "../../generated-sources/api/ai/model/ai-entity-reference";
import {AiSuggestedAction} from "../../generated-sources/api/ai/model/ai-suggested-action";

export type AiChatEvent =
  | { type: "message"; text: string }
  | { type: "tool_call"; tool: string; status: string }
  | { type: "entity_references"; refs: AiEntityReference[] }
  | { type: "suggested_actions"; actions: AiSuggestedAction[] }
  | { type: "done" }
  | { type: "error"; message: string };

@Injectable({providedIn: "root"})
export class AiChatService {
  private readonly accessTokenService = inject(LocalStorageAccessTokenService);

  streamChat(message: string, context: AiChatContext | null, conversationId: string): Observable<AiChatEvent> {
    return new Observable<AiChatEvent>(subscriber => {
      const token = this.accessTokenService.getAccessToken();
      const abortController = new AbortController();

      fetch("/api/ai/chat", {
        method: "POST",
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "Content-Type": "application/json",
          // eslint-disable-next-line @typescript-eslint/naming-convention
          ...(token ? {"Authorization": `Bearer ${token}`} : {}),
        },
        body: JSON.stringify({message, context, conversationId}),
        signal: abortController.signal,
      })
        .then(response => {
          if (!response.ok) {
            subscriber.next({type: "error", message: `HTTP ${response.status}: ${response.statusText}`});
            subscriber.complete();
            return;
          }
          return this.readStream(response.body!, subscriber);
        })
        .catch(err => {
          if (err.name !== "AbortError") {
            subscriber.next({type: "error", message: err.message || "Connection failed"});
          }
          subscriber.complete();
        });

      return () => abortController.abort();
    });
  }

  private async readStream(body: ReadableStream<Uint8Array>, subscriber: any): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const {done, value} = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, {stream: true});

        // SSE events are separated by double newline (\n\n)
        const eventBlocks = buffer.split("\n\n");
        // Last element may be incomplete — keep it in the buffer
        buffer = eventBlocks.pop() || "";

        for (const block of eventBlocks) {
          if (!block.trim()) continue;
          const event = this.parseSSEEvent(block);
          if (event) {
            subscriber.next(event);
            if (event.type === "done" || event.type === "error") {
              subscriber.complete();
              return;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
      subscriber.complete();
    }
  }

  private parseSSEEvent(eventBlock: string): AiChatEvent | null {
    const lines = eventBlock.split("\n");
    let eventType = "message";
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventType = line.substring(6).trim();
      } else if (line.startsWith("data:")) {
        // Spring SSE does NOT add a delimiter space after "data:" —
        // any space is part of the LLM token content (e.g. " scheint")
        dataLines.push(line.substring(5));
      }
    }

    if (dataLines.length === 0) return null;

    // Multiple data: lines in one event are joined with newlines (SSE spec)
    const data = dataLines.join("\n");

    // Route by event type
    switch (eventType) {
      case "done":
        return {type: "done"};
      case "error":
        return {type: "error", message: data};
      case "tool_call":
        try {
          const parsed = JSON.parse(data);
          return {type: "tool_call", tool: parsed.tool, status: parsed.status};
        } catch {
          return null;
        }
      case "entity_references":
        try {
          return {type: "entity_references", refs: JSON.parse(data)};
        } catch {
          return null;
        }
      case "suggested_actions":
        try {
          return {type: "suggested_actions", actions: JSON.parse(data)};
        } catch {
          return null;
        }
      case "message":
      default:
        return {type: "message", text: data};
    }
  }
}
